/**
 * Single source of truth for how a course's price is shown to the customer.
 *
 * Why this exists: the API always stores `amount` as the already-discounted
 * price, regardless of whether the discount has expired. It exposes the expiry
 * separately via `is_expire_discount` (true once `expire_discount_date` has
 * passed). Checkout (Cart.get_cart in the API) drops the discount once expired,
 * so the storefront must mirror that — otherwise it advertises a discounted
 * price the customer will never actually be charged.
 *
 * Rule: a discount is only "active" when it has NOT expired AND the discounted
 * `amount` actually differs from the original `amount_bf_discount`. When it is
 * not active we display the full `amount_bf_discount`, never the stale `amount`.
 */
export interface CoursePricing {
  /** An active, non-expired discount applies right now. */
  hasDiscount: boolean;
  /** Primary price to display (matches what checkout charges). */
  price: number;
  /** Pre-discount price, shown struck-through only when `hasDiscount` is true. */
  originalPrice: number;
}

interface CoursePriceFields {
  amount?: number;
  amount_bf_discount?: number;
  // API returns a JSON boolean; the model historically typed it as string, so
  // accept both rather than trust the declared type.
  is_expire_discount?: boolean | string;
}

export function resolveCoursePricing(course: CoursePriceFields | null | undefined): CoursePricing {
  const amount = course?.amount ?? 0;
  const originalPrice = course?.amount_bf_discount ?? amount;
  const expired = course?.is_expire_discount === true || course?.is_expire_discount === "true";
  const hasDiscount = !expired && amount !== originalPrice;

  return {
    hasDiscount,
    price: hasDiscount ? amount : originalPrice,
    originalPrice,
  };
}
